import { toPlatform } from '../lib/platform';
import type {
  CountryCount,
  CountryItem,
  CountryRef,
  LeadDoneReason,
  OutreachState,
  ParamChange,
  ParamSummaryEntry,
  RepeatRunsResponse,
  RepeatRunsSummary,
  RepeatSchedule,
  RunAddedCreator,
  RunAddedResponse,
  RunPoint,
  RunStatus,
  ScheduleHealth,
  ScheduleRun,
  ScheduleRunsResponse,
} from '../api/types';

/**
 * Reads the repeat-runs responses defensively. The portal can go live before
 * the discovery service does, and a schedule row from an older server has no
 * folder, health or run history — so every field has a stated fallback here
 * rather than an `undefined` surfacing somewhere in a table cell.
 *
 * Fallbacks never invent data: a count the server didn't send is `null` and
 * renders as "—", not 0.
 */

type Raw = Record<string, unknown>;

const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const HEALTHS: readonly ScheduleHealth[] = ['earning', 'dry', 'failing', 'paused', 'new'];
const STATUSES: readonly RunStatus[] = ['running', 'done', 'stopped', 'failed'];

// ── Settings, in words (older servers send none of this) ────────────────────

function countryRef(raw: unknown): CountryRef | null {
  if (!isObj(raw)) return null;
  const code = str(raw.code);
  return code ? { code, name: str(raw.name) ?? code } : null;
}

const countryRefs = (raw: unknown): CountryRef[] =>
  (Array.isArray(raw) ? raw : []).map(countryRef).filter((c): c is CountryRef => c !== null);

function countryItem(raw: unknown): CountryItem | null {
  if (!isObj(raw)) return null;
  if (raw.kind === 'group') {
    const id = str(raw.id);
    if (!id) return null;
    const countries = countryRefs(raw.countries);
    return { kind: 'group', id, name: str(raw.name) ?? id, count: num(raw.count) ?? countries.length, countries };
  }
  if (raw.kind === 'unknown') return { kind: 'unknown', raw: str(raw.raw) ?? '' };
  const code = str(raw.code);
  return code ? { kind: 'country', code, name: str(raw.name) ?? code } : null;
}

export function normaliseSummary(raw: unknown): ParamSummaryEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ParamSummaryEntry[] = [];
  for (const r of raw) {
    if (!isObj(r)) continue;
    const key = str(r.key);
    if (!key) continue;
    out.push({
      key,
      label: str(r.label) ?? key,
      value: typeof r.value === 'string' ? r.value : String(r.value ?? ''),
      group: str(r.group) ?? 'Other',
      groupLabel: str(r.groupLabel) ?? str(r.group) ?? 'Other',
      ...(Array.isArray(r.items)
        ? { items: r.items.map(countryItem).filter((c): c is CountryItem => c !== null) }
        : {}),
      ...(Array.isArray(r.expanded) ? { expanded: countryRefs(r.expanded) } : {}),
      ...(r.inactive === true ? { inactive: true } : {}),
      ...(str(r.note) ? { note: str(r.note)! } : {}),
    });
  }
  return out;
}

export function normaliseChanges(raw: unknown): ParamChange[] {
  if (!Array.isArray(raw)) return [];
  const out: ParamChange[] = [];
  for (const r of raw) {
    if (!isObj(r)) continue;
    const key = str(r.key);
    if (!key) continue;
    const added = countryRefs(r.countriesAdded);
    const removed = countryRefs(r.countriesRemoved);
    out.push({
      key,
      label: str(r.label) ?? key,
      before: typeof r.before === 'string' ? r.before : null,
      after: typeof r.after === 'string' ? r.after : null,
      ...(added.length ? { countriesAdded: added } : {}),
      ...(removed.length ? { countriesRemoved: removed } : {}),
    });
  }
  return out;
}

function normaliseCountries(raw: unknown): CountryCount[] {
  if (!Array.isArray(raw)) return [];
  const out: CountryCount[] = [];
  for (const r of raw) {
    if (!isObj(r)) continue;
    const n = num(r.count);
    if (n == null) continue;
    const country = str(r.country);
    out.push({ country, name: str(r.name) ?? country ?? 'Unknown', count: n, outsideTarget: r.outsideTarget === true });
  }
  return out;
}

/** Consecutive empty runs before a schedule counts as dry — mirrors the server. */
export const DRY_AFTER_RUNS = 3;

function runStatus(v: unknown): RunStatus {
  if ((STATUSES as readonly unknown[]).includes(v)) return v as RunStatus;
  // Raw job statuses, in case a server sends those.
  if (v === 'error') return 'failed';
  if (v === 'queued') return 'running';
  return 'done';
}

function runPoint(raw: unknown): RunPoint | null {
  if (!isObj(raw)) return null;
  const runAt = str(raw.runAt);
  if (!runAt) return null;
  return { jobId: str(raw.jobId) ?? runAt, runAt, added: num(raw.added) ?? 0, status: runStatus(raw.status) };
}

/** The server's rule, for rows that arrive without a health flag. */
export function deriveHealth(s: {
  enabled: boolean;
  runCount: number;
  lastAdded: number | null;
  recentRuns: RunPoint[];
}): { health: ScheduleHealth; dryRuns: number } {
  const finished = s.recentRuns.filter((r) => r.status !== 'running').slice().reverse();
  let dryRuns = 0;
  for (const r of finished) {
    if (r.status === 'failed' || r.added > 0) break;
    dryRuns++;
  }
  if (!s.enabled) return { health: 'paused', dryRuns };
  if (finished[0]?.status === 'failed') return { health: 'failing', dryRuns };
  if (dryRuns >= DRY_AFTER_RUNS) return { health: 'dry', dryRuns };
  if (finished.length === 0 && s.runCount === 0) return { health: 'new', dryRuns };
  return { health: 'earning', dryRuns };
}

export function normaliseSchedule(raw: unknown): RepeatSchedule | null {
  if (!isObj(raw)) return null;
  const publicId = str(raw.publicId);
  if (!publicId) return null;
  const enabled = raw.enabled !== false;
  const runCount = num(raw.runCount) ?? 0;
  const lastAdded = num(raw.lastAdded);
  const recentRuns = Array.isArray(raw.recentRuns)
    ? raw.recentRuns.map(runPoint).filter((r): r is RunPoint => r !== null)
    : [];
  const derived = deriveHealth({ enabled, runCount, lastAdded, recentRuns });
  const health = (HEALTHS as readonly unknown[]).includes(raw.health) ? (raw.health as ScheduleHealth) : derived.health;
  const folder = isObj(raw.folder) && str(raw.folder.publicId)
    ? {
        publicId: str(raw.folder.publicId)!,
        name: str(raw.folder.name) ?? 'Untitled folder',
        color: str(raw.folder.color),
        archived: raw.folder.archived === true,
      }
    : null;
  const createdAt = str(raw.createdAt);
  const historySince = str(raw.historySince);
  return {
    publicId,
    name: str(raw.name) ?? 'Scheduled search',
    platform: toPlatform(raw.platform),
    everyDays: num(raw.everyDays) ?? 1,
    enabled,
    pausedByArchive: raw.pausedByArchive === true || (!enabled && !!str(raw.pausedByArchiveAt)),
    // A reason only means something while the schedule is off.
    pausedReason: enabled ? null : str(raw.pausedReason),
    pausedAfterDryRuns: num(raw.pausedAfterDryRuns) ?? num(raw.pausedDryRuns),
    postsPer100: num(raw.postsPer100),
    nextRunAt: str(raw.nextRunAt) ?? '',
    lastRunAt: str(raw.lastRunAt),
    lastJobId: str(raw.lastJobId),
    lastAdded,
    totalAdded: num(raw.totalAdded) ?? 0,
    runCount,
    runsRecorded: num(raw.runsRecorded),
    createdByName: str(raw.createdByName),
    createdAt,
    historySince,
    // Absent = nothing to warn about; only an explicit false shows the notice.
    historyComplete: raw.historyComplete !== false,
    folder,
    health,
    dryRuns: num(raw.dryRuns) ?? derived.dryRuns,
    recentRuns,
    params: isObj(raw.params) ? raw.params : null,
    paramsSummary: normaliseSummary(raw.paramsSummary),
    paramsUpdatedAt: str(raw.paramsUpdatedAt),
    paramsUpdatedByName: str(raw.paramsUpdatedByName),
    outsideTargetTotal: num(raw.outsideTargetTotal),
  };
}

const DAY_MS = 86_400_000;

/** The summary strip from the rows alone, for a server that doesn't send one. */
export function deriveSummary(schedules: RepeatSchedule[], now = Date.now()): RepeatRunsSummary {
  const anyHistory = schedules.some((s) => s.recentRuns.length > 0);
  const within = (days: number) =>
    schedules.flatMap((s) => s.recentRuns).filter((r) => now - new Date(r.runAt).getTime() <= days * DAY_MS);
  const sum = (runs: RunPoint[]) => runs.reduce((n, r) => n + r.added, 0);
  return {
    total: schedules.length,
    active: schedules.filter((s) => s.enabled).length,
    paused: schedules.filter((s) => !s.enabled).length,
    dry: schedules.filter((s) => s.health === 'dry').length,
    failing: schedules.filter((s) => s.health === 'failing').length,
    // Without run history there is nothing to count from — say so, not 0.
    runsLast7Days: anyHistory ? within(7).length : null,
    addedLast7Days: anyHistory ? sum(within(7)) : null,
    addedLast30Days: anyHistory ? sum(within(30)) : null,
  };
}

export function normaliseRepeatRuns(raw: unknown, now = Date.now()): RepeatRunsResponse {
  const list = Array.isArray(raw) ? raw : isObj(raw) && Array.isArray(raw.schedules) ? raw.schedules : [];
  const schedules = list.map(normaliseSchedule).filter((s): s is RepeatSchedule => s !== null);
  const derived = deriveSummary(schedules, now);
  const given = isObj(raw) && isObj(raw.summary) ? raw.summary : {};
  return {
    schedules,
    summary: {
      total: schedules.length,
      active: num(given.active) ?? derived.active,
      paused: num(given.paused) ?? derived.paused,
      dry: num(given.dry) ?? derived.dry,
      failing: num(given.failing) ?? derived.failing,
      runsLast7Days: num(given.runsLast7Days) ?? derived.runsLast7Days,
      addedLast7Days: num(given.addedLast7Days) ?? derived.addedLast7Days,
      addedLast30Days: num(given.addedLast30Days) ?? derived.addedLast30Days,
    },
  };
}

function normaliseRun(raw: unknown): ScheduleRun | null {
  if (!isObj(raw)) return null;
  const jobId = str(raw.jobId);
  const runAt = str(raw.runAt) ?? str(raw.startedAt);
  if (!jobId || !runAt) return null;
  return {
    jobId,
    runAt,
    startedAt: str(raw.startedAt),
    finishedAt: str(raw.finishedAt),
    durationMs: num(raw.durationMs),
    status: runStatus(raw.status),
    stopReason: str(raw.stopReason),
    scanned: num(raw.scanned),
    added: num(raw.added) ?? 0,
    addedInFolder: num(raw.addedInFolder),
    skippedKnown: num(raw.skippedKnown),
    filteredOut: num(raw.filteredOut),
    failed: num(raw.failed),
    apiCalls: num(raw.apiCalls),
    error: str(raw.error),
    settingsDiffer: raw.settingsDiffer === true,
    countries: normaliseCountries(raw.countries),
    outsideTargetCount: num(raw.outsideTargetCount),
    unknownCountryCount: num(raw.unknownCountryCount),
  };
}

export function normaliseRuns(raw: unknown): ScheduleRunsResponse {
  const o = isObj(raw) ? raw : {};
  const runs = (Array.isArray(o.runs) ? o.runs : []).map(normaliseRun).filter((r): r is ScheduleRun => r !== null);
  return {
    schedule: normaliseSchedule(o.schedule),
    runs,
    page: num(o.page) ?? 1,
    pageSize: num(o.pageSize) ?? Math.max(runs.length, 1),
    total: num(o.total) ?? runs.length,
  };
}

function normaliseCreator(raw: unknown): RunAddedCreator | null {
  if (!isObj(raw)) return null;
  const username = str(raw.username);
  if (!username) return null;
  return {
    username,
    platform: toPlatform(raw.platform),
    nickname: str(raw.nickname),
    followers: num(raw.followers),
    avgViews: num(raw.avgViews),
    savedAt: str(raw.savedAt),
    outreachState: (str(raw.outreachState) ?? 'none') as OutreachState,
    outreachClaimedByName: str(raw.outreachClaimedByName),
    suppressedAt: str(raw.suppressedAt),
    doneReason: str(raw.doneReason) as LeadDoneReason | null,
    country: countryRef(raw.country),
    contentLanguage: str(raw.contentLanguage),
    outsideTarget: raw.outsideTarget === true,
  };
}

export function normaliseAdded(raw: unknown, jobId: string): RunAddedResponse {
  const o = isObj(raw) ? raw : {};
  const creators = (Array.isArray(o.creators) ? o.creators : [])
    .map(normaliseCreator)
    .filter((c): c is RunAddedCreator => c !== null);
  const ref = (v: unknown) => (isObj(v) && str(v.publicId) ? { publicId: str(v.publicId)!, name: str(v.name) ?? '' } : null);
  return {
    jobId: str(o.jobId) ?? jobId,
    runAt: str(o.runAt),
    added: num(o.added),
    folder: ref(o.folder),
    schedule: ref(o.schedule),
    targetCountries: Array.isArray(o.targetCountries)
      ? o.targetCountries.filter((c): c is string => typeof c === 'string')
      : null,
    outsideTargetCount: num(o.outsideTargetCount),
    countries: normaliseCountries(o.countries),
    paramsSummary: normaliseSummary(o.paramsSummary),
    settingsDiffer: o.settingsDiffer === true,
    settingsChanges: normaliseChanges(o.settingsChanges),
    creators,
    page: num(o.page) ?? 1,
    pageSize: num(o.pageSize) ?? Math.max(creators.length, 1),
    total: num(o.total) ?? creators.length,
  };
}

// ── Wording ─────────────────────────────────────────────────────────────────

export const HEALTH_FILTERS: { value: ScheduleHealth; label: string }[] = [
  { value: 'earning', label: 'Earning' },
  { value: 'dry', label: 'Dry' },
  { value: 'failing', label: 'Failing' },
  { value: 'paused', label: 'Paused' },
  { value: 'new', label: 'No runs yet' },
];

/** Paused by the sweeper for adding nobody, rather than by a person. */
export function pausedForDry(s: Pick<RepeatSchedule, 'enabled' | 'pausedReason'>): boolean {
  return !s.enabled && s.pausedReason === 'dry';
}

/** How many empty runs in a row got it paused; null when the server didn't count them. */
export function pausedDryRunCount(s: Pick<RepeatSchedule, 'pausedAfterDryRuns' | 'dryRuns'>): number | null {
  return s.pausedAfterDryRuns ?? (s.dryRuns > 0 ? s.dryRuns : null);
}

type HealthFields = Pick<RepeatSchedule, 'health' | 'dryRuns'> &
  Partial<Pick<RepeatSchedule, 'enabled' | 'pausedReason' | 'pausedAfterDryRuns' | 'pausedByArchive'>>;

export function healthLabel(s: HealthFields): string {
  if (s.enabled === false && s.pausedReason === 'dry') {
    const n = pausedDryRunCount({ pausedAfterDryRuns: s.pausedAfterDryRuns ?? null, dryRuns: s.dryRuns });
    return n ? `Paused: dry for ${n} run${n === 1 ? '' : 's'}` : 'Paused: dry';
  }
  switch (s.health) {
    case 'earning':
      return 'Earning';
    case 'dry':
      return `Dry for ${s.dryRuns} run${s.dryRuns === 1 ? '' : 's'}`;
    case 'failing':
      return 'Failing';
    case 'paused':
      return 'Paused';
    default:
      return 'No runs yet';
  }
}

export function healthHint(s: HealthFields): string {
  if (s.enabled === false && s.pausedReason === 'dry') {
    const n = pausedDryRunCount({ pausedAfterDryRuns: s.pausedAfterDryRuns ?? null, dryRuns: s.dryRuns });
    return `Paused automatically: ${n ? `its last ${n} runs` : 'several runs in a row'} added nobody. Change the search, then resume it.`;
  }
  switch (s.health) {
    case 'earning':
      return 'Its latest run added new creators.';
    case 'dry':
      return `The last ${s.dryRuns} runs added nobody. It is still spending search budget — pause it, or change the search.`;
    case 'failing':
      return 'The latest run failed. Open the run history for the error.';
    case 'paused':
      return s.pausedByArchive
        ? 'Paused when its folder was archived. Restoring the folder resumes it.'
        : 'Switched off. It will not run until resumed.';
    default:
      return 'It has not finished a run yet.';
  }
}

export function cadenceLabel(everyDays: number): string {
  return everyDays === 1 ? 'Daily' : everyDays === 7 ? 'Weekly' : `Every ${everyDays} days`;
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export function formatRunDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "—" for a count the server didn't record; a real zero stays "0". */
export const count = (n: number | null): string => (n == null ? '—' : n.toLocaleString());

// ── Settings wording ────────────────────────────────────────────────────────

/** Settings keys that say where creators / their audience are. Shown first. */
const COUNTRY_KEYS = ['creatorCountries', 'audienceCountries'];

/** The "Runs with" line: countries first, then the rest, capped so a row stays one line. */
export function runsWithLine(summary: ParamSummaryEntry[], max = 5): { shown: ParamSummaryEntry[]; more: number } {
  const active = summary.filter((e) => !e.inactive);
  const ordered = [
    ...active.filter((e) => COUNTRY_KEYS.includes(e.key)),
    ...active.filter((e) => !COUNTRY_KEYS.includes(e.key)),
  ];
  return { shown: ordered.slice(0, max), more: Math.max(0, ordered.length - max) };
}

/** Summary entries grouped the way the search form groups them, in first-seen order. */
export function groupSummary(summary: ParamSummaryEntry[]): { label: string; entries: ParamSummaryEntry[] }[] {
  const out: { label: string; entries: ParamSummaryEntry[] }[] = [];
  for (const e of summary) {
    const g = out.find((x) => x.label === e.groupLabel);
    if (g) g.entries.push(e);
    else out.push({ label: e.groupLabel, entries: [e] });
  }
  return out;
}

/** "US 18 · GB 4 · DE 2", capped, with the remainder counted. */
export function countryBreakdownLabel(countries: CountryCount[], max = 4): string {
  if (countries.length === 0) return '';
  const parts = countries.slice(0, max).map((c) => `${c.country ?? 'Unknown'} ${c.count}`);
  const rest = countries.slice(max).reduce((n, c) => n + c.count, 0);
  return rest > 0 ? `${parts.join(' · ')} · +${rest} more` : parts.join(' · ');
}

/** `UAE memes — weekly!` → `uae-memes-weekly`, for a download filename. */
export function fileSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'schedule';
}
